import React, { useState, useEffect, useRef } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { API_CONFIG } from '../config';
import { useTranslation } from '../hooks/useTranslation';

interface ConflictItem {
  id: string;
  description: string;
  amount: number;
  existingLiabilityId?: string;
  existingAmount?: number;
}

interface FeeConflict {
  type: 'producer' | 'coordinator' | 'supervisor' | 'finder';
  description: string;
  newAmount: number;
  existingAmount: number;
  existingId: string;
}

interface OrphanedItem {
  transactionId: string;
  description: string;
  amount: number;
  date?: string;
  hasPayment?: boolean;
}

interface ProtectedItem {
  assignmentId?: string;
  transactionId: string;
  description?: string;
  amount: number;
  reason: string;
}

interface ConflictCheckResult {
  hasConflicts: boolean;
  conflicts: ConflictItem[];
  feeConflicts: FeeConflict[];
  newItems: ConflictItem[];
  newFees: { type: string; description: string; amount: number }[];
  alreadyApproved: ConflictItem[];
  orphanedItems?: OrphanedItem[];
  protectedItems?: ProtectedItem[];
}

interface BulkApproveModalProps {
  isOpen: boolean;
  onClose: () => void;
  shotIds: string[];
  biddingSheetId?: string; // For analyzing finance diff
  onApprove: (specializations: string[], options?: { 
    skipConflicts?: boolean; 
    updateFees?: boolean; 
    updateLiabilities?: boolean;
    deleteOrphaned?: boolean;
  }) => Promise<void>;
}

const BulkApproveModal: React.FC<BulkApproveModalProps> = ({
  isOpen,
  onClose,
  shotIds,
  biddingSheetId,
  onApprove
}) => {
  const { t } = useTranslation();
  const [availableSpecializations, setAvailableSpecializations] = useState<string[]>([]);
  const [selectedSpecializations, setSelectedSpecializations] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [fetchingSpecs, setFetchingSpecs] = useState(false);
  const [error, setError] = useState<string>('');
  const [allApproved, setAllApproved] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Conflict state
  const [conflictCheckResult, setConflictCheckResult] = useState<ConflictCheckResult | null>(null);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [skipConflicts, setSkipConflicts] = useState(true);
  const [updateFees, setUpdateFees] = useState(false);
  const [deleteOrphaned, setDeleteOrphaned] = useState(false); // NEW: Option to delete orphaned liabilities
  
  // Finance diff state (for already approved assignments)
  const [financeDiff, setFinanceDiff] = useState<any>(null);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  
  // Track previous shotIds to avoid unnecessary refetches
  const prevShotIdsRef = useRef<string>('');
  const shotIdsKey = shotIds.join(',');

  // Fetch available specializations from expense assignments for selected shots
  useEffect(() => {
    if (isOpen && shotIds.length > 0 && shotIdsKey !== prevShotIdsRef.current) {
      prevShotIdsRef.current = shotIdsKey;
      setDataLoaded(false);
      setConflictCheckResult(null);
      setShowConflictDialog(false);
      fetchSpecializations();
    } else if (!isOpen) {
      // Reset only when modal closes
      prevShotIdsRef.current = '';
      setAvailableSpecializations([]);
      setSelectedSpecializations(new Set());
      setAllApproved(false);
      setDataLoaded(false);
      setConflictCheckResult(null);
      setShowConflictDialog(false);
      setSkipConflicts(true);
      setUpdateFees(false);
      setDeleteOrphaned(false);
      setFinanceDiff(null);
      setShowSyncDialog(false);
    }
  }, [isOpen, shotIdsKey]);

  const fetchSpecializations = async () => {
    setFetchingSpecs(true);
    setAllApproved(false);
    try {
      const token = localStorage.getItem('spellbook_token');
      const specializationsSet = new Set<string>();
      let hasAnyPending = false;
      let hasAnyAssignment = false;

      // Fetch expense assignments for each shot
      for (const shotId of shotIds) {
        const response = await fetch(`${API_CONFIG.API_URL}/bidding/shots/${shotId}/assignments`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          const expenseAssignments = data.expense || data.expenseAssignments || [];
          
          // Collect specializations from pending assignments (or assignments with artist that are not yet approved)
          expenseAssignments.forEach((assignment: any) => {
            if (assignment.artistId) {
              hasAnyAssignment = true;
              // Include if status is Pending, null, undefined, or empty string
              const isPending = !assignment.status || assignment.status === 'Pending';
              if (isPending && assignment.specialization) {
                specializationsSet.add(assignment.specialization);
                hasAnyPending = true;
              }
            }
          });
        }
      }

      // ALWAYS check finance diff if we have a biddingSheetId - regardless of approval status
      if (biddingSheetId) {
        try {
          console.log('[BulkApproveModal] Calling analyze-finance-diff for:', biddingSheetId);
          const diffResponse = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/analyze-finance-diff`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          if (diffResponse.ok) {
            const diffData = await diffResponse.json();
            console.log('[BulkApproveModal] Finance diff result:', diffData.summary);
            setFinanceDiff(diffData);
            
            // Check if there are ANY differences that need attention
            const hasDifferences = diffData.summary.new > 0 || 
                                   diffData.summary.updated > 0 || 
                                   diffData.summary.orphaned > 0 || 
                                   diffData.summary.feeChanges > 0;
            
            console.log('[BulkApproveModal] Has differences:', hasDifferences, 'hasAnyPending:', hasAnyPending);
            
            // If all assignments are approved AND there are differences, show sync dialog
            if (hasAnyAssignment && !hasAnyPending) {
              setAllApproved(true);
              if (hasDifferences) {
                setShowSyncDialog(true);
              }
            } else if (!hasAnyPending && hasDifferences) {
              // No pending items but there are finance differences - show sync dialog
              setShowSyncDialog(true);
            }
          } else {
            console.error('[BulkApproveModal] analyze-finance-diff failed:', diffResponse.status);
          }
        } catch (err) {
          console.error('[BulkApproveModal] Error checking finance diff:', err);
        }
      }

      // Set allApproved if no pending assignments
      if (hasAnyAssignment && !hasAnyPending) {
        setAllApproved(true);
      }

      setAvailableSpecializations(Array.from(specializationsSet).sort());
      setDataLoaded(true);
    } catch (err) {
      console.error('Error fetching specializations:', err);
      setError(t('bulkApprove.fetchError') || 'Не удалось загрузить список департаментов');
      setDataLoaded(true);
    } finally {
      setFetchingSpecs(false);
    }
  };

  const checkConflicts = async (): Promise<ConflictCheckResult | null> => {
    try {
      const token = localStorage.getItem('spellbook_token');
      
      // Check conflicts via original endpoint
      const conflictResponse = await fetch(`${API_CONFIG.API_URL}/bidding/expense-assignments/check-conflicts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          shotIds,
          specializations: Array.from(selectedSpecializations)
        })
      });

      let result: ConflictCheckResult | null = null;
      if (conflictResponse.ok) {
        result = await conflictResponse.json();
      }

      // Also get finance diff analysis if biddingSheetId is provided
      if (biddingSheetId) {
        try {
          const diffResponse = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/analyze-finance-diff`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          if (diffResponse.ok) {
            const diffData = await diffResponse.json();
            if (result) {
              result.orphanedItems = diffData.orphanedItems || [];
              result.protectedItems = diffData.protectedItems || [];
              // Mark as having conflicts if there are orphaned items
              if (diffData.orphanedItems?.length > 0) {
                result.hasConflicts = true;
              }
            }
          }
        } catch (err) {
          console.error('Error analyzing finance diff:', err);
        }
      }

      return result;
    } catch (err) {
      console.error('Error checking conflicts:', err);
      return null;
    }
  };

  const handleToggleSpecialization = (spec: string) => {
    const newSelected = new Set(selectedSpecializations);
    if (newSelected.has(spec)) {
      newSelected.delete(spec);
    } else {
      newSelected.add(spec);
    }
    setSelectedSpecializations(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedSpecializations(new Set(availableSpecializations));
  };

  const handleDeselectAll = () => {
    setSelectedSpecializations(new Set());
  };

  const handleApprove = async () => {
    if (selectedSpecializations.size === 0) {
      setError(t('bulkApprove.selectAtLeastOne') || 'Выберите хотя бы один департамент');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // First, check for conflicts
      const conflicts = await checkConflicts();
      
      if (conflicts && (conflicts.hasConflicts || conflicts.feeConflicts.length > 0)) {
        setConflictCheckResult(conflicts);
        setShowConflictDialog(true);
        setLoading(false);
        return;
      }

      // No conflicts, proceed with approval
      await onApprove(Array.from(selectedSpecializations));
      onClose();
    } catch (err: any) {
      setError(err.message || (t('bulkApprove.approveError') || 'Ошибка при утверждении расходов'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmWithConflicts = async () => {
    setLoading(true);
    setError('');

    try {
      // updateFees controls both fee updates AND liability updates
      await onApprove(Array.from(selectedSpecializations), { 
        skipConflicts: skipConflicts && !updateFees, 
        updateFees,
        updateLiabilities: updateFees,  // When updating fees, also update assignment liability amounts
        deleteOrphaned  // NEW: Delete orphaned liabilities if requested
      });
      onClose();
    } catch (err: any) {
      setError(err.message || (t('bulkApprove.approveError') || 'Ошибка при утверждении расходов'));
    } finally {
      setLoading(false);
    }
  };

  // Handle finance sync for already approved assignments
  const handleSyncFinance = async () => {
    if (!biddingSheetId) {
      setError('Не указан ID сметы');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/sync-finance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ deleteOrphaned })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Ошибка синхронизации');
      }

      const result = await response.json();
      console.log('Sync result:', result);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Ошибка при синхронизации финансов');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `₽${amount.toLocaleString()}`;
  };

  // Render sync dialog for already approved assignments with finance differences
  if (showSyncDialog && financeDiff) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Синхронизация финансов"
        size="lg"
      >
        <div className="space-y-4">
          {/* Summary */}
          <div className="bg-blue-900/20 border border-blue-500/30 rounded p-3">
            <p className="text-blue-400 text-sm">
              Все назначения уже утверждены, но обнаружены расхождения с финансовыми записями.
            </p>
          </div>

          {/* New items (missing liabilities) */}
          {financeDiff.newItems?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-green-400">
                Отсутствующие обязательства ({financeDiff.newItems.length})
              </h4>
              <div className="bg-gray-800 rounded p-2 max-h-40 overflow-y-auto">
                {financeDiff.newItems.map((item: any) => (
                  <div key={item.assignmentId} className="flex justify-between items-center py-1 text-sm border-b border-gray-700 last:border-0">
                    <span className="text-gray-300">{item.shotName} — {item.specialization} — {item.artistName}</span>
                    <span className="text-green-400">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400">
                Эти обязательства будут созданы при синхронизации
              </p>
            </div>
          )}

          {/* Updated items (changed amounts) */}
          {financeDiff.updatedItems?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-amber-400">
                Изменённые суммы ({financeDiff.updatedItems.length})
              </h4>
              <div className="bg-gray-800 rounded p-2 max-h-40 overflow-y-auto">
                {financeDiff.updatedItems.map((item: any) => (
                  <div key={item.assignmentId} className="flex justify-between items-center py-1 text-sm border-b border-gray-700 last:border-0">
                    <span className="text-gray-300">{item.shotName} — {item.artistName}</span>
                    <div className="text-right">
                      <span className="text-red-400 line-through mr-2">{formatCurrency(item.oldAmount)}</span>
                      <span className="text-green-400">{formatCurrency(item.newAmount)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Orphaned items */}
          {financeDiff.orphanedItems?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-red-400">
                Устаревшие обязательства ({financeDiff.orphanedItems.length})
              </h4>
              <div className="bg-red-900/20 border border-red-500/30 rounded p-2 max-h-40 overflow-y-auto">
                {financeDiff.orphanedItems.map((item: any) => (
                  <div key={item.transactionId} className="flex justify-between items-center py-1 text-sm border-b border-red-700/30 last:border-0">
                    <span className="text-gray-300">{item.description}</span>
                    <span className="text-red-400">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Protected items */}
          {financeDiff.protectedItems?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-500">
                Защищённые транзакции ({financeDiff.protectedItems.length})
              </h4>
              <div className="bg-gray-800/50 border border-gray-600 rounded p-2 max-h-32 overflow-y-auto opacity-70">
                {financeDiff.protectedItems.slice(0, 5).map((item: any, idx: number) => (
                  <div key={idx} className="flex justify-between items-center py-1 text-sm border-b border-gray-700 last:border-0">
                    <span className="text-gray-400">{item.shotName || item.reason}</span>
                    <span className="text-gray-500">{formatCurrency(item.amount || item.paidAmount)}</span>
                  </div>
                ))}
                {financeDiff.protectedItems.length > 5 && (
                  <div className="text-xs text-gray-500 pt-1">...и ещё {financeDiff.protectedItems.length - 5}</div>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Эти транзакции уже оплачены и не будут изменены
              </p>
            </div>
          )}

          {/* Fee changes */}
          {financeDiff.feeChanges?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-purple-400">
                Изменения в гонорарах ({financeDiff.feeChanges.length})
              </h4>
              <div className="bg-gray-800 rounded p-2">
                {financeDiff.feeChanges.map((fee: any, idx: number) => (
                  <div key={idx} className="flex justify-between items-center py-1 text-sm border-b border-gray-700 last:border-0">
                    <span className="text-gray-300">{fee.name} ({fee.type})</span>
                    <div className="text-right">
                      {fee.oldAmount > 0 && <span className="text-red-400 line-through mr-2">{formatCurrency(fee.oldAmount)}</span>}
                      <span className="text-green-400">{formatCurrency(fee.newAmount)}</span>
                      {fee.isPaid && <span className="text-xs text-red-400 ml-2">(оплачено)</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Options */}
          <div className="space-y-3 pt-2 border-t border-gray-700">
            {financeDiff.orphanedItems?.length > 0 && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteOrphaned}
                  onChange={(e) => setDeleteOrphaned(e.target.checked)}
                  className="w-4 h-4 text-red-600 bg-gray-800 border-gray-600 rounded focus:ring-red-500"
                />
                <span className="text-sm text-red-300">
                  Удалить устаревшие обязательства ({financeDiff.orphanedItems.length} шт.)
                </span>
              </label>
            )}
          </div>

          {error && (
            <div className="text-red-400 text-sm">{error}</div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
            <Button
              onClick={onClose}
              variant="secondary"
              disabled={loading}
            >
              Отмена
            </Button>
            <Button
              onClick={handleSyncFinance}
              disabled={loading || (
                financeDiff.newItems?.length === 0 && 
                financeDiff.updatedItems?.length === 0 && 
                financeDiff.feeChanges?.length === 0 &&
                financeDiff.orphanedItems?.length === 0 &&
                !deleteOrphaned
              )}
            >
              {loading ? 'Синхронизация...' : 'Синхронизировать'}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // Render conflict dialog
  if (showConflictDialog && conflictCheckResult) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('bulkApprove.conflictsFound') || 'Обнаружены конфликты'}
        size="lg"
      >
        <div className="space-y-4">
          {/* Summary */}
          <div className="bg-yellow-900/20 border border-yellow-500/30 rounded p-3">
            <p className="text-yellow-400 text-sm">
              {t('bulkApprove.conflictsDescription') || 'Некоторые позиции уже существуют в финансовых обязательствах. Выберите действие:'}
            </p>
          </div>

          {/* Task conflicts */}
          {conflictCheckResult.conflicts.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-orange-400">
                {t('bulkApprove.existingTasks') || 'Уже существующие задачи'} ({conflictCheckResult.conflicts.length})
              </h4>
              <div className="bg-gray-800 rounded p-2 max-h-40 overflow-y-auto">
                {conflictCheckResult.conflicts.map((item) => (
                  <div key={item.id} className="flex justify-between items-center py-1 text-sm border-b border-gray-700 last:border-0">
                    <span className="text-gray-300">{item.description}</span>
                    <span className="text-orange-400">{formatCurrency(item.existingAmount || item.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fee conflicts */}
          {conflictCheckResult.feeConflicts.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-orange-400">
                {t('bulkApprove.existingFees') || 'Существующие гонорары'} ({conflictCheckResult.feeConflicts.length})
              </h4>
              <div className="bg-gray-800 rounded p-2">
                {conflictCheckResult.feeConflicts.map((fee, idx) => (
                  <div key={idx} className="flex justify-between items-center py-1 text-sm border-b border-gray-700 last:border-0">
                    <span className="text-gray-300">{fee.description}</span>
                    <div className="text-right">
                      <div className="text-orange-400">{t('bulkApprove.current') || 'Текущая'}: {formatCurrency(fee.existingAmount)}</div>
                      <div className="text-green-400">{t('bulkApprove.new') || 'Новая'}: {formatCurrency(fee.newAmount)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New items to be added */}
          {conflictCheckResult.newItems.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-green-400">
                {t('bulkApprove.newTasks') || 'Новые задачи для добавления'} ({conflictCheckResult.newItems.length})
              </h4>
              <div className="bg-gray-800 rounded p-2 max-h-40 overflow-y-auto">
                {conflictCheckResult.newItems.map((item) => (
                  <div key={item.id} className="flex justify-between items-center py-1 text-sm border-b border-gray-700 last:border-0">
                    <span className="text-gray-300">{item.description}</span>
                    <span className="text-green-400">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New fees to be added */}
          {conflictCheckResult.newFees.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-green-400">
                {t('bulkApprove.newFees') || 'Новые гонорары для добавления'} ({conflictCheckResult.newFees.length})
              </h4>
              <div className="bg-gray-800 rounded p-2">
                {conflictCheckResult.newFees.map((fee, idx) => (
                  <div key={idx} className="flex justify-between items-center py-1 text-sm border-b border-gray-700 last:border-0">
                    <span className="text-gray-300">{fee.description}</span>
                    <span className="text-green-400">{formatCurrency(fee.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Orphaned items (liabilities without corresponding assignments) */}
          {conflictCheckResult.orphanedItems && conflictCheckResult.orphanedItems.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-red-400">
                Устаревшие обязательства ({conflictCheckResult.orphanedItems.length})
              </h4>
              <div className="bg-red-900/20 border border-red-500/30 rounded p-2 max-h-40 overflow-y-auto">
                {conflictCheckResult.orphanedItems.map((item) => (
                  <div key={item.transactionId} className="flex justify-between items-center py-1 text-sm border-b border-red-700/30 last:border-0">
                    <span className="text-gray-300">{item.description}</span>
                    <span className="text-red-400">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400">
                Эти обязательства не соответствуют текущим назначениям в смете
              </p>
            </div>
          )}

          {/* Protected items (paid - cannot be modified) */}
          {conflictCheckResult.protectedItems && conflictCheckResult.protectedItems.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-500">
                Защищённые транзакции ({conflictCheckResult.protectedItems.length})
              </h4>
              <div className="bg-gray-800/50 border border-gray-600 rounded p-2 max-h-32 overflow-y-auto opacity-70">
                {conflictCheckResult.protectedItems.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center py-1 text-sm border-b border-gray-700 last:border-0">
                    <span className="text-gray-400">{item.description || item.reason}</span>
                    <span className="text-gray-500">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                Эти транзакции уже оплачены и не могут быть изменены
              </p>
            </div>
          )}

          {/* Options */}
          <div className="space-y-3 pt-2 border-t border-gray-700">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={skipConflicts}
                onChange={(e) => {
                  setSkipConflicts(e.target.checked);
                  if (e.target.checked) setUpdateFees(false);
                }}
                className="w-4 h-4 text-indigo-600 bg-gray-800 border-gray-600 rounded focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-300">
                {t('bulkApprove.skipExisting') || 'Пропустить существующие позиции (добавить только новые)'}
              </span>
            </label>

            {(conflictCheckResult.conflicts.length > 0 || conflictCheckResult.feeConflicts.length > 0) && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={updateFees}
                  onChange={(e) => {
                    setUpdateFees(e.target.checked);
                    if (e.target.checked) setSkipConflicts(false);
                  }}
                  className="w-4 h-4 text-green-600 bg-gray-800 border-gray-600 rounded focus:ring-green-500"
                />
                <span className="text-sm text-gray-300">
                  {t('bulkApprove.updateExisting') || 'Обновить суммы существующих позиций и гонораров'}
                </span>
              </label>
            )}

            {/* NEW: Delete orphaned option */}
            {conflictCheckResult.orphanedItems && conflictCheckResult.orphanedItems.length > 0 && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteOrphaned}
                  onChange={(e) => setDeleteOrphaned(e.target.checked)}
                  className="w-4 h-4 text-red-600 bg-gray-800 border-gray-600 rounded focus:ring-red-500"
                />
                <span className="text-sm text-red-300">
                  Удалить устаревшие обязательства ({conflictCheckResult.orphanedItems.length} шт.)
                </span>
              </label>
            )}
          </div>

          {error && (
            <div className="text-red-400 text-sm">{error}</div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
            <Button
              onClick={() => {
                setShowConflictDialog(false);
                setConflictCheckResult(null);
              }}
              variant="secondary"
              disabled={loading}
            >
              {t('common.back') || 'Назад'}
            </Button>
            <Button
              onClick={onClose}
              variant="secondary"
              disabled={loading}
            >
              {t('common.cancel') || 'Отмена'}
            </Button>
            <Button
              onClick={handleConfirmWithConflicts}
              disabled={loading || (conflictCheckResult.newItems.length === 0 && conflictCheckResult.newFees.length === 0 && !updateFees)}
            >
              {loading ? (t('bulkApprove.approving') || 'Утверждение...') : (t('bulkApprove.confirm') || 'Подтвердить')}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('bulkApprove.title') || 'Отправить в финансы'}
      size="md"
    >
      <div className="space-y-4 min-h-[200px]">
        <p className="text-gray-300">
          {t('bulkApprove.selectedShots') || 'Выбрано шотов'}: <span className="font-semibold">{shotIds.length}</span>
        </p>

        {fetchingSpecs || !dataLoaded ? (
          <div className="text-gray-400 text-sm flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500 mr-2"></div>
            {t('common.loading') || 'Загрузка...'}
          </div>
        ) : allApproved ? (
          <div className="text-yellow-400 text-sm bg-yellow-900/20 p-3 rounded border border-yellow-500/30">
            {t('bulkApprove.allAlreadyApproved') || 'Все назначения для выбранных шотов уже утверждены'}
          </div>
        ) : availableSpecializations.length === 0 ? (
          <div className="text-gray-400 text-sm">
            {t('bulkApprove.noDepartmentsAvailable') || 'Нет доступных департаментов для утверждения. Убедитесь, что в расходной смете назначены артисты.'}
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-300">
                {t('bulkApprove.selectDepartments') || 'Выберите департаменты для утверждения:'}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={handleSelectAll}
                  className="text-xs text-indigo-400 hover:text-indigo-300"
                >
                  {t('bulkApprove.selectAll') || 'Выбрать все'}
                </button>
                <button
                  onClick={handleDeselectAll}
                  className="text-xs text-gray-400 hover:text-gray-300"
                >
                  {t('bulkApprove.deselectAll') || 'Снять все'}
                </button>
              </div>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {availableSpecializations.map((spec) => (
                <label
                  key={spec}
                  className="flex items-center p-2 bg-gray-700 rounded hover:bg-gray-600 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedSpecializations.has(spec)}
                    onChange={() => handleToggleSpecialization(spec)}
                    className="mr-3 w-4 h-4 text-indigo-600 bg-gray-800 border-gray-600 rounded focus:ring-indigo-500"
                  />
                  <span className="text-white">{spec}</span>
                </label>
              ))}
            </div>
          </>
        )}

        {error && (
          <div className="text-red-400 text-sm">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
          <Button
            onClick={onClose}
            variant="secondary"
            disabled={loading}
          >
            {t('bulkApprove.cancel') || 'Отмена'}
          </Button>
          <Button
            onClick={handleApprove}
            disabled={loading || selectedSpecializations.size === 0}
          >
            {loading ? (t('bulkApprove.approving') || 'Утверждение...') : (t('bulkApprove.approve') || 'Утвердить')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkApproveModal;
